export type BriefGenre =
  | 'fantasy' | 'historical' | 'horror' | 'literary-speculative'
  | 'literary-thriller' | 'mystery' | 'romance' | 'romantasy' | 'sci-fi' | 'ya';

export type TurnShapeKind =
  | 'question' | 'reflection' | 'propose-to-react' | 'closing'
  | 'transition-acknowledge' | 'chip-pick-single' | 'chip-pick-multi' | 'free-text-with-example';

export interface ChipOption {
  id: string;
  label: string;
  subtext?: string;
}

export interface TurnShape {
  kind: TurnShapeKind;
  angleChips?: string[];
  draftTitle?: string;
  draftBody?: string;
  draftMeta?: string;
  summary?: string;
  nextTopicHint?: string;
  prompt?: string;
  options?: ChipOption[];
  minSelect?: number;
  maxSelect?: number;
  fieldLabel?: string;
  exampleText?: string;
  placeholder?: string;
}

export interface ClusterDetail {
  id: string;
  name: string;
  status: 'done' | 'active' | 'parked' | 'partial' | 'pending';
}

export interface BriefProgress {
  currentCluster: string;
  clusterIndex: number;
  clusterTotal: number;
  dots: ('done' | 'active' | 'pending')[];
  clusterDetails: ClusterDetail[];
}

export type SessionStatus = 'active' | 'finalising' | 'complete' | 'failed' | 'abandoned';

export interface BriefTurnResponse {
  sessionId: string;
  turnNumber: number;
  status: SessionStatus;
  turn: {
    text: string;
    turnShape: TurnShape;
  };
  progress: BriefProgress;
}

export interface BriefSessionResponse {
  sessionId: string;
  status: SessionStatus;
  turnNumber: number;
  lastTurn: { text: string; turnShape: TurnShape } | null;
  progress: BriefProgress;
  recentTranscript: Array<{ role: 'user' | 'assistant'; content: string }>;
  judgeFeeback?: string;
}

export type CustomerAction =
  | { kind: 'message'; text: string }
  | { kind: 'park' }
  | { kind: 'accept-draft' }
  | { kind: 'edit-draft'; text: string }
  | { kind: 'request-variants' }
  | { kind: 'reject-draft'; reason: string }
  | { kind: 'angle-chip'; chipLabel: string }
  | { kind: 'acknowledge-transition' }
  | { kind: 'chip-pick'; selectedIds: string[] }
  | { kind: 'free-text-field'; text: string };

export async function briefStart(genre: BriefGenre): Promise<BriefTurnResponse> {
  const res = await fetch('/api/brief/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ genre }),
  });

  if (res.status === 409) {
    const data = await res.json();
    throw Object.assign(new Error('Active session exists'), { code: 'ACTIVE_SESSION_EXISTS', sessionId: data.sessionId });
  }

  if (!res.ok) throw new Error(`POST /api/brief/start failed: ${res.status}`);
  return res.json();
}

export async function briefTurn(sessionId: string, action: CustomerAction): Promise<BriefTurnResponse> {
  const res = await fetch('/api/brief/turn', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sessionId, action }),
  });

  if (res.status === 409) {
    const data = await res.json();
    throw Object.assign(new Error(data.error), { code: data.error });
  }

  if (!res.ok) throw new Error(`POST /api/brief/turn failed: ${res.status}`);
  return res.json();
}

export async function briefGetSession(sessionId: string): Promise<BriefSessionResponse> {
  const res = await fetch(`/api/brief/session/${sessionId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok) throw new Error(`GET /api/brief/session failed: ${res.status}`);
  return res.json();
}

export async function briefAbandon(sessionId: string): Promise<{ ok: true }> {
  const res = await fetch('/api/brief/abandon', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sessionId }),
  });

  if (!res.ok) throw new Error(`POST /api/brief/abandon failed: ${res.status}`);
  return { ok: true };
}

// --- Streaming turn API ---

export interface StreamTurnCallbacks {
  onDelta: (text: string) => void;
  onDone: (result: { turnShape: TurnShape; progress: BriefProgress; sessionStatus: SessionStatus; turnNumber: number }) => void;
  onError: (error: Error) => void;
}

const STREAM_URL = import.meta.env.VITE_BRIEF_TURN_STREAM_URL ?? '/api/brief/turn-stream';

export function briefTurnStream(
  sessionId: string,
  action: CustomerAction,
  callbacks: StreamTurnCallbacks,
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const res = await fetch(STREAM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sessionId, action }),
        signal: controller.signal,
      });

      if (!res.ok) {
        callbacks.onError(new Error(`POST /api/brief/turn-stream failed: ${res.status}`));
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        callbacks.onError(new Error('No response body for streaming'));
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const json = line.slice(6);
          if (!json) continue;

          try {
            const event = JSON.parse(json) as
              | { type: 'delta'; text: string }
              | { type: 'done'; turnShape: TurnShape; progress: BriefProgress; sessionStatus: SessionStatus; turnNumber: number }
              | { type: 'error'; message: string; statusCode?: number };

            switch (event.type) {
              case 'delta':
                callbacks.onDelta(event.text);
                break;
              case 'done':
                callbacks.onDone({
                  turnShape: event.turnShape,
                  progress: event.progress,
                  sessionStatus: event.sessionStatus,
                  turnNumber: event.turnNumber,
                });
                break;
              case 'error':
                callbacks.onError(new Error(event.message));
                break;
            }
          } catch {
            // Skip malformed JSON lines
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        callbacks.onError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  })();

  return controller;
}

export interface BriefQuota {
  used: number;
  limit: number;
  canStart: boolean;
  creditBalance: number;
}

export async function briefGetQuota(): Promise<BriefQuota> {
  const res = await fetch('/api/brief/quota', {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok) throw new Error(`GET /api/brief/quota failed: ${res.status}`);
  return res.json();
}
